// ── EstimationCard ────────────────────────────────────────────────────────────
//
// CFGIA.B1 (#1541) — shared estimation-display radio group, extracted from
// SettingsOrganisationPage. Used by SettingsEstimationPage (Settings surface)
// and EstimationPreferenceStep (wizard). Instant-save on change via
// PATCH /tenants/:id. canEdit=false renders the group read-only.
//
// data-testid="settings-estimation-*" mirrors the Organisation-page testids
// so existing assertions continue to pass under the new location.

import { useState, useEffect } from 'react';
import { apiFetch } from '../../lib/api';
import type { EstimationType } from '../../hooks/useMyTenant';

interface Props {
  tenantId: string;
  canEdit?: boolean;
  showIntro?: boolean;
  initialValue?: EstimationType;
  onSaved?: (value: EstimationType) => void;
}

const OPTIONS = [
  { value: 'STORY_POINTS' as const, label: 'Story Points',       desc: 'Traditional agile relative complexity measure' },
  { value: 'EU_RU'         as const, label: 'EU / RU',            desc: 'Execution Units + Review Units (map to real minutes)' },
  { value: 'BOTH'          as const, label: 'Both — side by side', desc: 'Show story points and EU/RU with equal weight' },
] as const;

export default function EstimationCard({
  tenantId,
  canEdit = true,
  showIntro = true,
  initialValue,
  onSaved,
}: Props) {
  const [estimationType, setEstimationType] = useState<EstimationType>(initialValue ?? 'BOTH');

  useEffect(() => {
    apiFetch<{ estimationType: EstimationType }>(`/tenants/${tenantId}`)
      .then(t => { if (t.estimationType) setEstimationType(t.estimationType); })
      .catch(() => {});
  }, [tenantId]);

  const handleChange = (value: EstimationType) => {
    if (!canEdit) return;
    setEstimationType(value);
    apiFetch(`/tenants/${tenantId}`, {
      method: 'PATCH',
      body: JSON.stringify({ estimationType: value }),
    })
      .then(() => onSaved?.(value))
      .catch(() => {});
  };

  return (
    <div>
      {showIntro && (
        <>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>
            Estimation Display
          </h2>
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
            Choose how effort estimates appear across your projects.
          </p>
        </>
      )}
      {OPTIONS.map(opt => (
        <label
          key={opt.value}
          data-testid={`settings-estimation-${opt.value}`}
          style={{
            display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10,
            padding: '10px 12px', borderRadius: 'var(--radius-sm)',
            cursor: canEdit ? 'pointer' : 'default',
            border: `1px solid ${estimationType === opt.value ? 'var(--primary)' : 'var(--border)'}`,
            background: estimationType === opt.value ? 'rgba(124,58,237,0.04)' : 'transparent',
          }}
        >
          <input
            type="radio"
            name={`estimationType-${tenantId}`}
            value={opt.value}
            checked={estimationType === opt.value}
            onChange={() => handleChange(opt.value)}
            disabled={!canEdit}
            style={{ accentColor: 'var(--primary)', marginTop: 2 }}
          />
          <div>
            <span style={{ fontFamily: 'var(--font-display)', fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
              {opt.label}
            </span>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', margin: '2px 0 0' }}>
              {opt.desc}
            </p>
          </div>
        </label>
      ))}
    </div>
  );
}
